import { action, makeObservable } from "mobx";
import { createRef } from "react";
import { injectable } from "tsyringe";
import { ViewModel } from "@orderofchaos/mvvm";

export interface GoogleLoginWidgetViewProps {
  clientId: string;
  onCredential: (idToken: string) => void;
  onError?: (message: string) => void;
  disabled?: boolean;
  text?: GoogleRenderButtonOptions["text"];
  width?: number;
}

const GOOGLE_GSI_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

let googleScriptPromise: Promise<void> | null = null;

function ensureGoogleScriptLoaded(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("window is not available"));
  }

  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }

  if (googleScriptPromise) {
    return googleScriptPromise;
  }

  googleScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GOOGLE_GSI_SCRIPT_SRC}"]`,
    );
    if (existing) {
      if (existing.dataset.loaded === "true" || window.google?.accounts?.id) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Не удалось загрузить Google GSI script")),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.src = GOOGLE_GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () =>
      reject(new Error("Не удалось загрузить Google GSI script"));
    document.head.appendChild(script);
  })
    .catch((error) => {
      googleScriptPromise = null;
      throw error;
    })
    .then(() => undefined);

  return googleScriptPromise;
}

@injectable()
export class GoogleLoginWidgetViewModel extends ViewModel<
  unknown,
  GoogleLoginWidgetViewProps
> {
  readonly containerRef = createRef<HTMLDivElement>();
  private disposed = false;

  constructor() {
    super();
    makeObservable(this);
  }

  protected onViewMounted(): void {
    void this.renderGoogleButton();
    this.reaction(
      () => [
        this.viewProps.clientId,
        this.viewProps.text,
        this.viewProps.width,
      ],
      () => {
        void this.renderGoogleButton();
      },
    );
    this.addDisposer(() => {
      this.disposed = true;
      this.cleanupContainer();
    });
  }

  @action.bound
  private handleCredentialResponse(response: GoogleCredentialResponse): void {
    if (this.viewProps.disabled) {
      return;
    }

    const token =
      typeof response.credential === "string" ? response.credential.trim() : "";
    if (!token) {
      this.viewProps.onError?.(
        "Google не вернул credential. Попробуйте повторить вход.",
      );
      return;
    }

    this.viewProps.onCredential(token);
  }

  private cleanupContainer(): void {
    const container = this.containerRef.current;
    if (!container) {
      return;
    }

    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
  }

  private async renderGoogleButton(): Promise<void> {
    const container = this.containerRef.current;
    if (!container) {
      return;
    }

    this.cleanupContainer();
    const clientId = this.viewProps.clientId.trim();
    if (!clientId) {
      return;
    }

    try {
      await ensureGoogleScriptLoaded();
      if (this.disposed) {
        return;
      }

      const googleId = window.google?.accounts?.id;
      if (!googleId) {
        this.viewProps.onError?.(
          "Google SDK не инициализировался. Проверьте блокировку скриптов.",
        );
        return;
      }

      googleId.initialize({
        client_id: clientId,
        callback: this.handleCredentialResponse,
        ux_mode: "popup",
        context: "signin",
        cancel_on_tap_outside: true,
      });

      googleId.renderButton(container, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: this.viewProps.text || "continue_with",
        shape: "pill",
        logo_alignment: "left",
        width: this.viewProps.width || 300,
        locale: "ru",
      });
    } catch (error) {
      this.viewProps.onError?.(
        error instanceof Error
          ? error.message
          : "Не удалось отрисовать Google кнопку",
      );
    }
  }
}
